/**
 * Admin boost pricing dashboard page for /admin/boost-pricing route.
 * Interactive pricing control grid with real cost data and editable pricing parameters.
 */

import { useState, useMemo, useCallback } from "react";
import { Navigate } from "react-router-dom";
import { PageHeader } from "../components/layout/PageHeader";
import { useProfile } from "../contexts/ProfileContext";
import {
  useAdminCostsByOperation,
  useAdminPricingConfig,
  useAdminBoostGateConfig,
  useAdminUpdateBoostGateConfig,
} from "../hooks/useAdminWorkspaces";
import type { OperationCost, PricingConfigEntry } from "../services/naraDoor";
import {
  AlertCircle,
  Loader2,
  DollarSign,
  Zap,
  BarChart3,
  Hash,
  ArrowUpDown,
  Settings2,
  Shield,
  Check,
} from "lucide-react";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  Legend,
} from "recharts";
import { publicRoutes } from "../lib/routes";

type SortField =
  | "service"
  | "processor"
  | "yourCost"
  | "avgBoostsPerTxn"
  | "medianBoostsPerTxn"
  | "revenuePerTxn"
  | "transactionCount"
  | "margin"
  | "providerUsdPerUnit"
  | "boostsPerUnit"
;
type SortDir = "asc" | "desc";

const PERIOD_OPTIONS = [
  { label: "7d", days: 7 },
  { label: "30d", days: 30 },
  { label: "90d", days: 90 },
  { label: "365d", days: 365 },
];

const PIE_COLORS = [
  "#f97316", // orange-500
  "#3b82f6", // blue-500
  "#10b981", // emerald-500
  "#8b5cf6", // violet-500
  "#ef4444", // red-500
  "#06b6d4", // cyan-500
  "#f59e0b", // amber-500
  "#ec4899", // pink-500
];

function formatCurrency(value: number): string {
  return `$${value.toFixed(4)}`;
}

function formatCurrencyShort(value: number): string {
  return `$${value.toFixed(2)}`;
}

function formatNumber(value: number): string {
  return value.toLocaleString();
}

/** Map a cost-data row to its pricing config key */
function getPricingKey(op: OperationCost): string {
  if (op.service === "gemini") {
    // Use input pricing as representative
    return `${op.processor}:input`;
  }
  // STT (chirp_3) and DocAI (form_parser) use processor directly
  return op.processor;
}

interface PricingOverride {
  providerUsdPerUnit?: number;
  boostsPerUnit?: number;
}

interface GridRow {
  service: string;
  processor: string;
  medianBoostsPerTxn: number;
  avgBoostsPerTxn: number;
  transactionCount: number;
  totalProviderCostUsd: number;
  totalBoostsCharged: number;
  totalPromptTokens: number;
  totalOutputTokens: number;
  totalAudioDurationSec: number;
  totalPages: number;
  providerUsdPerUnit: number | null;
  boostsPerUnit: number | null;
  hasPricingConfig: boolean;
  pricingKey: string;
}

export function AdminBoostPricing() {
  const { isAdmin, isLoading: isProfileLoading } = useProfile();
  const [days, setDays] = useState(30);
  const [sortField, setSortField] = useState<SortField>("margin");
  const [sortDir, setSortDir] = useState<SortDir>("desc");
  const [packPrice, setPackPrice] = useState(5);
  const [overrides, setOverrides] = useState<Record<string, PricingOverride>>({});

  // Boost gate config state
  const [gateOverrides, setGateOverrides] = useState<Record<string, { minimumBoosts?: number; coverCharge?: number; minimumCost?: number }>>({});
  const [gateSaveSuccess, setGateSaveSuccess] = useState(false);
  const [gateSaveError, setGateSaveError] = useState<string | null>(null);

  const { data: costData, isLoading: isCostLoading, error: costError } =
    useAdminCostsByOperation(days);
  const { data: pricingData, isLoading: isPricingLoading, error: pricingError } =
    useAdminPricingConfig();
  const { data: gateData, isLoading: isGateLoading, error: gateError } =
    useAdminBoostGateConfig();
  const updateGateMutation = useAdminUpdateBoostGateConfig();

  if (!isProfileLoading && !isAdmin) {
    return <Navigate to={publicRoutes.home} replace />;
  }

  const isLoading = isCostLoading || isPricingLoading;
  const error = costError || pricingError;

  const pricePerBoost = packPrice / 500;

  // Build a map of pricing configs by operation_key
  const pricingMap = useMemo(() => {
    const map = new Map<string, PricingConfigEntry>();
    if (pricingData?.configs) {
      for (const c of pricingData.configs) {
        map.set(c.operationKey, c);
      }
    }
    return map;
  }, [pricingData]);

  // Build a map of cost data by pricing key for lookup
  const costMap = useMemo(() => {
    const map = new Map<string, OperationCost>();
    if (costData?.operations) {
      for (const op of costData.operations) {
        const key = getPricingKey(op);
        // Merge if multiple operations map to the same key (e.g. gemini input+output)
        const existing = map.get(key);
        if (existing) {
          existing.totalBoostsCharged += op.totalBoostsCharged;
          existing.totalProviderCostUsd = (existing.totalProviderCostUsd ?? 0) + (op.totalProviderCostUsd ?? 0);
          existing.transactionCount += op.transactionCount;
        } else {
          map.set(key, { ...op });
        }
      }
    }
    return map;
  }, [costData]);

  // Build grid rows — pricing-first: start from ALL config entries, left-join usage
  const gridRows = useMemo((): GridRow[] => {
    const rows: GridRow[] = [];
    const seenKeys = new Set<string>();

    // First: all pricing config entries (the rate card)
    if (pricingData?.configs) {
      for (const config of pricingData.configs) {
        // Skip output entries — we show input as representative for LLM
        if (config.operationKey.endsWith(":output")) continue;

        const key = config.operationKey;
        seenKeys.add(key);
        const op = costMap.get(key);

        // Derive service/processor from the config key
        let service = config.category === "llm" ? "gemini" : config.category === "stt" ? "speech_to_text" : "document_ai";
        let processor = key.replace(/:input$/, "");

        // If we have usage data, use its service/processor (more accurate)
        if (op) {
          service = op.service;
          processor = op.processor;
        }

        rows.push({
          service,
          processor,
          medianBoostsPerTxn: op?.medianBoostsPerTxn ?? 0,
          avgBoostsPerTxn: op?.avgBoostsPerTxn ?? 0,
          transactionCount: op?.transactionCount ?? 0,
          totalProviderCostUsd: op?.totalProviderCostUsd ?? 0,
          totalBoostsCharged: op?.totalBoostsCharged ?? 0,
          totalPromptTokens: op?.totalPromptTokens ?? 0,
          totalOutputTokens: op?.totalOutputTokens ?? 0,
          totalAudioDurationSec: op?.totalAudioDurationSec ?? 0,
          totalPages: op?.totalPages ?? 0,
          providerUsdPerUnit: config.providerUsdPerUnit,
          boostsPerUnit: config.boostsPerUnit,
          hasPricingConfig: true,
          pricingKey: key,
        });
      }
    }

    // Second: any usage data that doesn't match a config entry (legacy/unknown)
    if (costData?.operations) {
      for (const op of costData.operations) {
        const key = getPricingKey(op);
        if (!seenKeys.has(key)) {
          seenKeys.add(key);
          rows.push({
            service: op.service,
            processor: op.processor,
            medianBoostsPerTxn: op.medianBoostsPerTxn ?? 0,
            avgBoostsPerTxn: op.avgBoostsPerTxn ?? 0,
            transactionCount: op.transactionCount,
            totalProviderCostUsd: op.totalProviderCostUsd ?? 0,
            totalBoostsCharged: op.totalBoostsCharged,
            totalPromptTokens: op.totalPromptTokens ?? 0,
            totalOutputTokens: op.totalOutputTokens ?? 0,
            totalAudioDurationSec: op.totalAudioDurationSec ?? 0,
            totalPages: op.totalPages ?? 0,
            providerUsdPerUnit: null,
            boostsPerUnit: null,
            hasPricingConfig: false,
            pricingKey: key,
          });
        }
      }
    }

    return rows;
  }, [pricingData, costData, costMap]);

  // Apply overrides and compute derived values
  const getEffective = useCallback(
    (row: GridRow) => {
      const o = overrides[row.pricingKey];
      const providerUsdPerUnit = o?.providerUsdPerUnit ?? row.providerUsdPerUnit;
      const boostsPerUnit = o?.boostsPerUnit ?? row.boostsPerUnit;
      const changed =
        o !== undefined &&
        (o.providerUsdPerUnit !== undefined || o.boostsPerUnit !== undefined);

      const avgBoostsPerTxn = row.avgBoostsPerTxn;
      const yourCostPerTxn =
        row.transactionCount > 0
          ? row.totalProviderCostUsd / row.transactionCount
          : 0;
      const revenuePerTxn = avgBoostsPerTxn * pricePerBoost;
      const margin =
        revenuePerTxn > 0
          ? ((revenuePerTxn - yourCostPerTxn) / revenuePerTxn) * 100
          : 0;

      return { providerUsdPerUnit, boostsPerUnit, changed, margin, revenuePerTxn, yourCostPerTxn };
    },
    [overrides, pricePerBoost]
  );

  const sorted = useMemo(() => {
    return [...gridRows].sort((a, b) => {
      let aVal: string | number;
      let bVal: string | number;
      if (sortField === "providerUsdPerUnit" || sortField === "boostsPerUnit") {
        const ea = getEffective(a);
        const eb = getEffective(b);
        aVal = ea[sortField] ?? 0;
        bVal = eb[sortField] ?? 0;
      } else if (sortField === "margin") {
        aVal = getEffective(a).margin;
        bVal = getEffective(b).margin;
      } else if (sortField === "yourCost") {
        aVal = getEffective(a).yourCostPerTxn;
        bVal = getEffective(b).yourCostPerTxn;
      } else if (sortField === "revenuePerTxn") {
        aVal = getEffective(a).revenuePerTxn;
        bVal = getEffective(b).revenuePerTxn;
      } else {
        aVal = a[sortField] ?? "";
        bVal = b[sortField] ?? "";
      }
      if (aVal < bVal) return sortDir === "asc" ? -1 : 1;
      if (aVal > bVal) return sortDir === "asc" ? 1 : -1;
      return 0;
    });
  }, [gridRows, sortField, sortDir, getEffective]);

  // Bar chart data
  const barData = useMemo(() => {
    return [...gridRows]
      .sort((a, b) => b.totalBoostsCharged - a.totalBoostsCharged)
      .map((op) => ({
        name: `${op.service}/${op.processor}`,
        boosts: op.totalBoostsCharged,
      }));
  }, [gridRows]);

  // Avg provider cost bar chart data
  const avgCostBarData = useMemo(() => {
    return [...gridRows]
      .sort((a, b) => {
        const avgA = a.transactionCount > 0 ? a.totalProviderCostUsd / a.transactionCount : 0;
        const avgB = b.transactionCount > 0 ? b.totalProviderCostUsd / b.transactionCount : 0;
        return avgB - avgA;
      })
      .map((op) => ({
        name: `${op.service}/${op.processor}`,
        avg: op.transactionCount > 0 ? op.totalProviderCostUsd / op.transactionCount : 0,
      }));
  }, [gridRows]);

  // Raw usage summary for display
  const usageRows = useMemo(() => {
    return gridRows.map((op) => {
      const txn = op.transactionCount || 1;
      let usageLabel = "";
      if (op.service === "gemini") {
        const avgPrompt = op.totalPromptTokens / txn;
        const avgOutput = op.totalOutputTokens / txn;
        const avgTotal = avgPrompt + avgOutput;
        usageLabel = `${Math.round(avgPrompt).toLocaleString()} in / ${Math.round(avgOutput).toLocaleString()} out (${Math.round(avgTotal).toLocaleString()} total)`;
      } else if (op.service === "speech_to_text") {
        const avgAudioSec = op.totalAudioDurationSec / txn;
        const mins = avgAudioSec / 60;
        usageLabel = `${mins.toFixed(1)} min (${Math.round(avgAudioSec)}s)`;
      } else if (op.service === "document_ai") {
        const avgPages = op.totalPages / txn;
        usageLabel = `${avgPages.toFixed(1)} pages`;
      }
      const avgCost = op.totalProviderCostUsd / txn;
      return {
        name: `${op.service}/${op.processor}`,
        service: op.service,
        processor: op.processor,
        usage: usageLabel,
        avgCost,
        txnCount: op.transactionCount,
      };
    });
  }, [gridRows]);

  // Pie chart data
  const pieData = useMemo(() => {
    if (!gridRows.length) return [];
    const byService: Record<string, number> = {};
    for (const op of gridRows) {
      byService[op.service] = (byService[op.service] || 0) + op.totalProviderCostUsd;
    }
    return Object.entries(byService)
      .map(([name, value]) => ({ name, value }))
      .sort((a, b) => b.value - a.value);
  }, [gridRows]);

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    } else {
      setSortField(field);
      setSortDir("desc");
    }
  };

  const handleOverride = (
    pricingKey: string,
    field: "providerUsdPerUnit" | "boostsPerUnit",
    value: number
  ) => {
    setOverrides((prev) => ({
      ...prev,
      [pricingKey]: {
        ...prev[pricingKey],
        [field]: value,
      },
    }));
  };

  const SortHeader = ({
    field,
    label,
    className,
  }: {
    field: SortField;
    label: string;
    className?: string;
  }) => (
    <th
      className={`px-3 py-2 font-medium text-muted-foreground cursor-pointer hover:text-foreground select-none text-xs ${className || ""}`}
      onClick={() => handleSort(field)}
    >
      <span className="inline-flex items-center gap-1">
        {label}
        <ArrowUpDown
          className={`h-3 w-3 ${sortField === field ? "text-foreground" : "text-muted-foreground/40"}`}
        />
      </span>
    </th>
  );

  return (
    <div>
      <PageHeader
        title="Boost Pricing"
        description="Interactive pricing control grid with real cost data"
        isAdmin
        breadcrumbs={[
          { label: "Dashboard", href: publicRoutes.home },
          { label: "Admin" },
          { label: "Boost Pricing" },
        ]}
      />

      {/* Period Selector + Pack Price */}
      <div className="flex flex-wrap items-center gap-4 mb-6">
        <div className="flex items-center gap-2">
          <span className="text-sm font-medium text-muted-foreground">Period:</span>
          {PERIOD_OPTIONS.map((opt) => (
            <button
              key={opt.days}
              onClick={() => setDays(opt.days)}
              className={`px-3 py-1 text-sm rounded-md border transition-colors ${
                days === opt.days
                  ? "bg-primary text-primary-foreground border-primary"
                  : "bg-card text-muted-foreground border-border hover:bg-accent"
              }`}
            >
              {opt.label}
            </button>
          ))}
        </div>

        <div className="flex items-center gap-2">
          <span className="text-sm font-medium text-muted-foreground whitespace-nowrap">
            Boost pack:
          </span>
          <div className="relative">
            <span className="absolute left-2 top-1/2 -translate-y-1/2 text-sm text-muted-foreground">
              $
            </span>
            <input
              type="number"
              min={0.01}
              step={0.5}
              value={packPrice}
              onChange={(e) => setPackPrice(Math.max(0.01, Number(e.target.value)))}
              className="w-20 pl-6 pr-2 py-1 text-sm border border-border rounded-md bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary/50"
            />
          </div>
          <span className="text-xs text-muted-foreground">
            / 500 = ${pricePerBoost.toFixed(4)}/boost
          </span>
        </div>
      </div>

      {/* Workflow Boost Gates */}
      <div className="bg-card rounded-lg border border-border overflow-hidden mb-6">
        <div className="px-4 py-3 border-b border-border flex items-center justify-between">
          <h2 className="text-sm font-semibold text-foreground flex items-center gap-2">
            <Shield className="h-4 w-4" />
            Workflow Boost Gates
          </h2>
          {Object.keys(gateOverrides).length > 0 && (
            <button
              onClick={() => {
                setGateOverrides({});
                setGateSaveSuccess(false);
                setGateSaveError(null);
              }}
              className="text-xs text-muted-foreground hover:text-foreground underline"
            >
              Reset changes
            </button>
          )}
        </div>
        {isGateLoading ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
            <span className="ml-2 text-sm text-muted-foreground">
              Loading workflow gates...
            </span>
          </div>
        ) : gateError ? (
          <div className="flex items-center gap-3 p-4">
            <AlertCircle className="h-5 w-5 text-red-600 shrink-0" />
            <p className="text-sm text-red-600">
              {gateError instanceof Error ? gateError.message : String(gateError)}
            </p>
          </div>
        ) : !gateData?.workflows?.length ? (
          <p className="text-sm text-muted-foreground p-6">
            No workflow gates configured.
          </p>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-muted/50">
                    <th className="px-3 py-2 text-left font-medium text-muted-foreground text-xs">
                      Workflow Type
                    </th>
                    <th className="px-3 py-2 text-left font-medium text-muted-foreground text-xs">
                      Description
                    </th>
                    <th className="px-3 py-2 text-right font-medium text-muted-foreground text-xs">
                      Minimum Boosts
                    </th>
                    <th className="px-3 py-2 text-right font-medium text-muted-foreground text-xs">
                      Cover Charge
                    </th>
                    <th className="px-3 py-2 text-right font-medium text-muted-foreground text-xs">
                      Minimum Cost
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {gateData.workflows.map((wf) => {
                    const overrideEntry = gateOverrides[wf.workflowType] ?? {};
                    const currentMinBoosts = overrideEntry.minimumBoosts !== undefined ? overrideEntry.minimumBoosts : wf.minimumBoosts;
                    const currentCoverCharge = overrideEntry.coverCharge !== undefined ? overrideEntry.coverCharge : wf.coverCharge;
                    const currentMinCost = overrideEntry.minimumCost !== undefined ? overrideEntry.minimumCost : wf.minimumCost;
                    const isMinBoostsChanged = overrideEntry.minimumBoosts !== undefined;
                    const isCoverChargeChanged = overrideEntry.coverCharge !== undefined;
                    const isMinCostChanged = overrideEntry.minimumCost !== undefined;

                    const handleFieldChange = (field: "minimumBoosts" | "coverCharge" | "minimumCost", rawValue: string) => {
                      const val = Math.max(0, Math.floor(Number(rawValue)));
                      setGateOverrides((prev) => ({
                        ...prev,
                        [wf.workflowType]: { ...prev[wf.workflowType], [field]: val },
                      }));
                      setGateSaveSuccess(false);
                      setGateSaveError(null);
                    };

                    return (
                      <tr
                        key={wf.workflowType}
                        className="border-t border-border hover:bg-accent/50 transition-colors"
                      >
                        <td className="px-3 py-2 font-medium font-mono text-xs">
                          {wf.workflowType}
                        </td>
                        <td className="px-3 py-2 text-muted-foreground">
                          {wf.description}
                        </td>
                        <td className="px-3 py-2 text-right">
                          <input
                            type="number"
                            min={0}
                            step={1}
                            value={currentMinBoosts}
                            onChange={(e) => handleFieldChange("minimumBoosts", e.target.value)}
                            className={`w-20 text-right px-1 py-0.5 text-sm border rounded bg-background border-border ${
                              isMinBoostsChanged ? "ring-2 ring-orange-400" : ""
                            }`}
                          />
                        </td>
                        <td className="px-3 py-2 text-right">
                          <input
                            type="number"
                            min={0}
                            step={1}
                            value={currentCoverCharge}
                            onChange={(e) => handleFieldChange("coverCharge", e.target.value)}
                            className={`w-20 text-right px-1 py-0.5 text-sm border rounded bg-background border-border ${
                              isCoverChargeChanged ? "ring-2 ring-orange-400" : ""
                            }`}
                          />
                        </td>
                        <td className="px-3 py-2 text-right">
                          <input
                            type="number"
                            min={0}
                            step={1}
                            value={currentMinCost}
                            onChange={(e) => handleFieldChange("minimumCost", e.target.value)}
                            className={`w-20 text-right px-1 py-0.5 text-sm border rounded bg-background border-border ${
                              isMinCostChanged ? "ring-2 ring-orange-400" : ""
                            }`}
                          />
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            {Object.keys(gateOverrides).length > 0 && (
              <div className="px-4 py-3 border-t border-border flex items-center justify-end gap-3">
                <button
                  onClick={async () => {
                    setGateSaveError(null);
                    setGateSaveSuccess(false);
                    try {
                      const workflows = gateData.workflows.map((wf) => {
                        const overrideEntry = gateOverrides[wf.workflowType] ?? {};
                        return {
                          workflowType: wf.workflowType,
                          minimumBoosts: overrideEntry.minimumBoosts !== undefined ? overrideEntry.minimumBoosts : wf.minimumBoosts,
                          coverCharge: overrideEntry.coverCharge !== undefined ? overrideEntry.coverCharge : wf.coverCharge,
                          minimumCost: overrideEntry.minimumCost !== undefined ? overrideEntry.minimumCost : wf.minimumCost,
                        };
                      });
                      await updateGateMutation.mutateAsync({ workflows });
                      setGateOverrides({});
                      setGateSaveSuccess(true);
                    } catch (err) {
                      setGateSaveError(
                        err instanceof Error ? err.message : "Failed to save"
                      );
                    }
                  }}
                  disabled={updateGateMutation.isPending}
                  className="px-4 py-1.5 text-sm font-medium rounded-md bg-primary text-primary-foreground hover:bg-primary/90 disabled:opacity-50 flex items-center gap-2"
                >
                  {updateGateMutation.isPending ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <Check className="h-4 w-4" />
                  )}
                  Save
                </button>
                {gateSaveError && (
                  <span className="text-sm text-red-600">{gateSaveError}</span>
                )}
              </div>
            )}
            {gateSaveSuccess && Object.keys(gateOverrides).length === 0 && (
              <div className="px-4 py-2 border-t border-border">
                <span className="text-sm text-green-600 flex items-center gap-1">
                  <Check className="h-4 w-4" />
                  Boost gate config saved successfully.
                </span>
              </div>
            )}
          </>
        )}
      </div>

      {isLoading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
          <span className="ml-2 text-sm text-muted-foreground">
            Loading cost data...
          </span>
        </div>
      ) : error ? (
        <div className="flex items-center gap-3 p-4 bg-red-50 dark:bg-red-950 border border-red-200 dark:border-red-800 rounded-lg">
          <AlertCircle className="h-5 w-5 text-red-600 shrink-0" />
          <div>
            <p className="text-sm font-medium text-red-800 dark:text-red-200">
              Failed to load data
            </p>
            <p className="text-xs text-red-600">
              {error instanceof Error ? error.message : String(error)}
            </p>
          </div>
        </div>
      ) : (
        <>
          {/* Summary Cards */}
          {(() => {
            const totalProviderCost = costData?.totals.totalProviderCostUsd ?? 0;
            const totalRevenue = (costData?.totals.totalBoostsCharged ?? 0) * pricePerBoost;
            const totalMargin = totalRevenue - totalProviderCost;
            const totalMarginPct = totalRevenue > 0 ? (totalMargin / totalRevenue) * 100 : 0;
            return (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
                <div className="p-4 bg-red-50 dark:bg-red-950/30 border border-red-200 dark:border-red-800 rounded-lg">
                  <div className="flex items-center gap-2 mb-1">
                    <DollarSign className="h-4 w-4 text-red-600" />
                    <span className="text-xs font-medium text-red-700 dark:text-red-300 uppercase tracking-wide">
                      Your Cost
                    </span>
                  </div>
                  <p className="text-2xl font-bold text-red-900 dark:text-red-100">
                    {formatCurrencyShort(totalProviderCost)}
                  </p>
                  <p className="text-xs text-red-600 dark:text-red-400 mt-1">
                    What you pay providers
                  </p>
                </div>
                <div className="p-4 bg-green-50 dark:bg-green-950/30 border border-green-200 dark:border-green-800 rounded-lg">
                  <div className="flex items-center gap-2 mb-1">
                    <Zap className="h-4 w-4 text-green-600" />
                    <span className="text-xs font-medium text-green-700 dark:text-green-300 uppercase tracking-wide">
                      Customer Revenue
                    </span>
                  </div>
                  <p className="text-2xl font-bold text-green-900 dark:text-green-100">
                    {formatCurrencyShort(totalRevenue)}
                  </p>
                  <p className="text-xs text-green-600 dark:text-green-400 mt-1">
                    {formatNumber(costData?.totals.totalBoostsCharged ?? 0)} boosts charged
                  </p>
                </div>
                <div className="p-4 bg-purple-50 dark:bg-purple-950/30 border border-purple-200 dark:border-purple-800 rounded-lg">
                  <div className="flex items-center gap-2 mb-1">
                    <BarChart3 className="h-4 w-4 text-purple-600" />
                    <span className="text-xs font-medium text-purple-700 dark:text-purple-300 uppercase tracking-wide">
                      Margin
                    </span>
                  </div>
                  <p className={`text-2xl font-bold ${totalMargin >= 0 ? "text-purple-900 dark:text-purple-100" : "text-red-600 dark:text-red-400"}`}>
                    {formatCurrencyShort(totalMargin)}
                  </p>
                  <p className={`text-xs mt-1 ${totalMarginPct >= 50 ? "text-green-600" : totalMarginPct >= 0 ? "text-yellow-600" : "text-red-600"}`}>
                    {totalMarginPct.toFixed(1)}% margin
                  </p>
                </div>
                <div className="p-4 bg-blue-50 dark:bg-blue-950/30 border border-blue-200 dark:border-blue-800 rounded-lg">
                  <div className="flex items-center gap-2 mb-1">
                    <Hash className="h-4 w-4 text-blue-600" />
                    <span className="text-xs font-medium text-blue-700 dark:text-blue-300 uppercase tracking-wide">
                      Transactions
                    </span>
                  </div>
                  <p className="text-2xl font-bold text-blue-900 dark:text-blue-100">
                    {formatNumber(costData?.totals.transactionCount ?? 0)}
                  </p>
                </div>
              </div>
            );
          })()}

          {/* Interactive Pricing Grid */}
          <div className="bg-card rounded-lg border border-border overflow-hidden mb-6">
            <div className="px-4 py-3 border-b border-border flex items-center justify-between">
              <h2 className="text-sm font-semibold text-foreground flex items-center gap-2">
                <Settings2 className="h-4 w-4" />
                Cost & Pricing by Operation
              </h2>
              {Object.keys(overrides).length > 0 && (
                <button
                  onClick={() => setOverrides({})}
                  className="text-xs text-muted-foreground hover:text-foreground underline"
                >
                  Reset changes
                </button>
              )}
            </div>
            {sorted.length === 0 ? (
              <p className="text-sm text-muted-foreground p-6">
                No cost data found for the selected period.
              </p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="bg-muted/50">
                      <SortHeader field="service" label="Service" />
                      <SortHeader field="processor" label="Processor" />
                      <SortHeader
                        field="yourCost"
                        label="Your Cost"
                        className="text-right"
                      />
                      <SortHeader
                        field="avgBoostsPerTxn"
                        label="Avg Boosts"
                        className="text-right"
                      />
                      <SortHeader
                        field="medianBoostsPerTxn"
                        label="Median Boosts"
                        className="text-right"
                      />
                      <SortHeader
                        field="revenuePerTxn"
                        label="Revenue/Txn"
                        className="text-right"
                      />
                      <SortHeader
                        field="margin"
                        label="Margin"
                        className="text-right"
                      />
                      <SortHeader
                        field="providerUsdPerUnit"
                        label="USD/Unit"
                        className="text-right"
                      />
                      <SortHeader
                        field="boostsPerUnit"
                        label="Boosts/Unit"
                        className="text-right"
                      />
                    </tr>
                  </thead>
                  <tbody>
                    {sorted.map((row) => {
                      const eff = getEffective(row);
                      const marginColor =
                        eff.margin >= 50
                          ? "text-green-600 dark:text-green-400"
                          : eff.margin >= 0
                            ? "text-yellow-600 dark:text-yellow-400"
                            : "text-red-600 dark:text-red-400";

                      const inputBase =
                        "w-16 text-right px-1 py-0.5 text-sm border rounded bg-background";
                      const changedRing = "ring-2 ring-orange-400";

                      const isProviderChanged =
                        overrides[row.pricingKey]?.providerUsdPerUnit !== undefined;
                      const isBoostsChanged =
                        overrides[row.pricingKey]?.boostsPerUnit !== undefined;

                      return (
                        <tr
                          key={`${row.service}-${row.processor}`}
                          className="border-t border-border hover:bg-accent/50 transition-colors"
                        >
                          <td className="px-3 py-2 font-medium">{row.service}</td>
                          <td className="px-3 py-2 text-muted-foreground">
                            {row.processor}
                            {!row.hasPricingConfig && (
                              <span className="ml-1 text-xs text-muted-foreground/60">
                                (default)
                              </span>
                            )}
                          </td>
                          <td className="px-3 py-2 text-right tabular-nums text-red-600 dark:text-red-400">
                            {formatCurrency(eff.yourCostPerTxn)}
                          </td>
                          <td className="px-3 py-2 text-right tabular-nums">
                            {row.avgBoostsPerTxn.toFixed(1)}
                          </td>
                          <td className="px-3 py-2 text-right tabular-nums">
                            {row.medianBoostsPerTxn.toFixed(1)}
                          </td>
                          <td className="px-3 py-2 text-right tabular-nums text-green-600 dark:text-green-400">
                            {formatCurrency(eff.revenuePerTxn)}
                          </td>
                          <td
                            className={`px-3 py-2 text-right font-medium tabular-nums ${marginColor}`}
                          >
                            {eff.margin.toFixed(1)}%
                          </td>
                          <td className="px-3 py-2 text-right">
                            <input
                              type="number"
                              min={0}
                              step={0.0001}
                              value={eff.providerUsdPerUnit ?? ""}
                              placeholder="—"
                              onChange={(e) =>
                                handleOverride(
                                  row.pricingKey,
                                  "providerUsdPerUnit",
                                  Math.max(0, Number(e.target.value))
                                )
                              }
                              className={`${inputBase} border-border ${isProviderChanged ? changedRing : ""}`}
                            />
                          </td>
                          <td className="px-3 py-2 text-right">
                            <input
                              type="number"
                              min={0}
                              step={0.01}
                              value={eff.boostsPerUnit ?? ""}
                              placeholder="—"
                              onChange={(e) =>
                                handleOverride(
                                  row.pricingKey,
                                  "boostsPerUnit",
                                  Math.max(0, Number(e.target.value))
                                )
                              }
                              className={`${inputBase} border-border ${isBoostsChanged ? changedRing : ""}`}
                            />
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {/* Charts */}
          {gridRows.length > 0 && (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
              {/* Bar Chart */}
              <div className="bg-card rounded-lg border border-border p-4">
                <h3 className="text-sm font-semibold text-foreground mb-4 flex items-center gap-2">
                  <Zap className="h-4 w-4" />
                  Boosts Charged by Operation
                </h3>
                <ResponsiveContainer
                  width="100%"
                  height={Math.max(200, barData.length * 36)}
                >
                  <BarChart
                    data={barData}
                    layout="vertical"
                    margin={{ left: 120, right: 20 }}
                  >
                    <XAxis type="number" tick={{ fontSize: 12 }} />
                    <YAxis
                      type="category"
                      dataKey="name"
                      tick={{ fontSize: 11 }}
                      width={110}
                    />
                    <Tooltip
                      formatter={(value) => [
                        formatNumber(value as number),
                        "Boosts",
                      ]}
                      contentStyle={{
                        backgroundColor: "var(--color-card, #fff)",
                        border: "1px solid var(--color-border, #e5e7eb)",
                        borderRadius: "0.375rem",
                        fontSize: "0.75rem",
                      }}
                    />
                    <Bar dataKey="boosts" fill="#f97316" radius={[0, 4, 4, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>

              {/* Pie Chart */}
              <div className="bg-card rounded-lg border border-border p-4">
                <h3 className="text-sm font-semibold text-foreground mb-4 flex items-center gap-2">
                  <DollarSign className="h-4 w-4" />
                  Cost Distribution by Service
                </h3>
                <ResponsiveContainer width="100%" height={300}>
                  <PieChart>
                    <Pie
                      data={pieData}
                      dataKey="value"
                      nameKey="name"
                      cx="50%"
                      cy="50%"
                      outerRadius={100}
                      label={({ name, percent }) =>
                        `${name} (${((percent ?? 0) * 100).toFixed(1)}%)`
                      }
                      labelLine
                    >
                      {pieData.map((_entry, index) => (
                        <Cell
                          key={`cell-${index}`}
                          fill={PIE_COLORS[index % PIE_COLORS.length]}
                        />
                      ))}
                    </Pie>
                    <Tooltip
                      formatter={(value) => [
                        formatCurrencyShort(value as number),
                        "Cost",
                      ]}
                      contentStyle={{
                        backgroundColor: "var(--color-card, #fff)",
                        border: "1px solid var(--color-border, #e5e7eb)",
                        borderRadius: "0.375rem",
                        fontSize: "0.75rem",
                      }}
                    />
                    <Legend />
                  </PieChart>
                </ResponsiveContainer>
              </div>
            </div>
          )}

          {/* Avg/Median Provider Cost per Operation */}
          {avgCostBarData.length > 0 && (
            <div className="bg-card rounded-lg border border-border p-4 mb-6">
              <h3 className="text-sm font-semibold text-foreground mb-4 flex items-center gap-2">
                <DollarSign className="h-4 w-4" />
                Avg Provider Cost per Transaction (USD)
              </h3>
              <ResponsiveContainer
                width="100%"
                height={Math.max(200, avgCostBarData.length * 40)}
              >
                <BarChart
                  data={avgCostBarData}
                  layout="vertical"
                  margin={{ left: 140, right: 20 }}
                >
                  <XAxis
                    type="number"
                    tick={{ fontSize: 12 }}
                    tickFormatter={(v) => `$${v.toFixed(4)}`}
                  />
                  <YAxis
                    type="category"
                    dataKey="name"
                    tick={{ fontSize: 11 }}
                    width={130}
                  />
                  <Tooltip
                    formatter={(value) => [
                      `$${(value as number).toFixed(6)}`,
                      "Average",
                    ]}
                    contentStyle={{
                      backgroundColor: "var(--color-card, #fff)",
                      border: "1px solid var(--color-border, #e5e7eb)",
                      borderRadius: "0.375rem",
                      fontSize: "0.75rem",
                    }}
                  />
                  <Bar dataKey="avg" fill="#3b82f6" radius={[0, 4, 4, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          )}

          {/* Raw Usage per Operation */}
          {usageRows.length > 0 && (
            <div className="bg-card rounded-lg border border-border overflow-hidden mb-6">
              <div className="px-4 py-3 border-b border-border">
                <h2 className="text-sm font-semibold text-foreground flex items-center gap-2">
                  <BarChart3 className="h-4 w-4" />
                  Avg Usage & Cost per Operation
                </h2>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="bg-muted/50">
                      <th className="px-3 py-2 text-left font-medium text-muted-foreground text-xs">Service</th>
                      <th className="px-3 py-2 text-left font-medium text-muted-foreground text-xs">Model / Processor</th>
                      <th className="px-3 py-2 text-left font-medium text-muted-foreground text-xs">Avg Usage / Txn</th>
                      <th className="px-3 py-2 text-right font-medium text-muted-foreground text-xs">Avg Cost (USD)</th>
                      <th className="px-3 py-2 text-right font-medium text-muted-foreground text-xs">Transactions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {usageRows.map((row) => (
                      <tr
                        key={row.name}
                        className="border-t border-border hover:bg-accent/50 transition-colors"
                      >
                        <td className="px-3 py-2 font-medium">{row.service}</td>
                        <td className="px-3 py-2 text-muted-foreground">{row.processor}</td>
                        <td className="px-3 py-2 text-muted-foreground font-mono text-xs">
                          {row.usage || "—"}
                        </td>
                        <td className="px-3 py-2 text-right tabular-nums text-red-600 dark:text-red-400">
                          {formatCurrency(row.avgCost)}
                        </td>
                        <td className="px-3 py-2 text-right tabular-nums">
                          {formatNumber(row.txnCount)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}
